import type { TemplatePage } from '../services/interface';
import {
  ONBOARDING_VIDEO_REVIEWS_GIFS_BILLING_CODE,
  ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL,
  ONBOARDING_VIDEO_REVIEWS_GIFS_GENERATED_COUNT,
} from '../const/onboardingVideoReviews';

export const TEMPLATE_STUDIO_RUNTIME = 'template_studio' as const;
export const LEGACY_REACT_RUNTIME = 'legacy_react' as const;

export const TEMPLATE_STUDIO_RENDERERS = {
  html: 'html',
  image: 'image',
} as const;

export const TEMPLATE_STUDIO_AI_PROCESSES = {
  htmlSectionGeneration: 'html_section_generation',
  sectionImageGeneration: 'section_image_generation',
  videoReviewsGeneration: 'section_video_reviews_generation',
} as const;

export const VIDEO_REVIEWS_TEMPLATE_HTML = `<section data-contract-version="sophisticated-html-v2" data-template-version="2" data-stype="video_reviews" data-role="section" data-fx-element="video-reviews" data-element-family="video-reviews" data-variant="video-reviews-v01">
  <div data-role="video-reviews-inner">
    <div data-role="section-heading">
      <p data-role="section-eyebrow">EN USO</p>
      <h2 data-role="section-title">Mira como se usa</h2>
    </div>
    <div data-role="video-reviews-carousel" aria-label="Videos cortos del producto en uso">
      <article data-role="video-review-card" data-video-review-index="1" aria-label="Video corto 1">
        <img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==" alt="Video corto del producto en uso 1" data-role="video-review-gif" data-video-review-index="1" data-fluxi-img-size="medium" loading="lazy" decoding="async" width="420" height="746" />
      </article>
      <article data-role="video-review-card" data-video-review-index="2" aria-label="Video corto 2">
        <img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==" alt="Video corto del producto en uso 2" data-role="video-review-gif" data-video-review-index="2" data-fluxi-img-size="medium" loading="lazy" decoding="async" width="420" height="746" />
      </article>
      <article data-role="video-review-card" data-video-review-index="3" aria-label="Video corto 3">
        <img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==" alt="Video corto del producto en uso 3" data-role="video-review-gif" data-video-review-index="3" data-fluxi-img-size="medium" loading="lazy" decoding="async" width="420" height="746" />
      </article>
      <article data-role="video-review-card" data-video-review-index="4" aria-label="Video corto 4">
        <img src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==" alt="Video corto del producto en uso 4" data-role="video-review-gif" data-video-review-index="4" data-fluxi-img-size="medium" loading="lazy" decoding="async" width="420" height="746" />
      </article>
    </div>
  </div>
</section>`;

export const VIDEO_REVIEWS_SECTION_INSTRUCTIONS = `Seccion video_reviews HTML V2 para una landing page ecommerce.

Objetivo:
- Adaptar los textos visibles al producto y al angulo de venta.
- Mantener una seccion breve, clara y natural que introduzca un carrusel horizontal de GIFs UGC.

Reglas:
- Conserva exactamente la estructura de carrusel y los 4 slots de GIF.
- Conserva data-role="video-review-card", data-role="video-review-gif" y data-video-review-index.
- No reemplaces los src de los GIFs. Esos src son slots internos y el sistema los reemplazara despues.
- No agregues placehold.co, imagenes nuevas, videos nuevos, CSS, estilos inline, clases nuevas ni scripts.
- Puedes cambiar solo textos visibles como eyebrow, titulo, aria-label y alt text para adaptarlos al producto.
- El copy debe sonar natural, no generico, y conectar con el producto sin prometer resultados imposibles.`;

const STANDARD_SECTION_MAP: Record<string, { type: string; pageId: string }> = {
  faq: { type: 'faqSection', pageId: 'faqs' },
  'cta-1': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
  'cta-2': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
  'cta-3': { type: 'buyNowFreeShippingSection', pageId: 'buy_now_free_shipping' },
  warranty: { type: 'guaranteeSection', pageId: 'guarantee_section' },
};

const definedMetadata = (metadata: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(metadata).filter(([, value]) => value !== undefined && value !== null && value !== ''));

const sectionUuid = (prefix: string) => `${prefix}_${crypto.randomUUID()}`;

type TemplateStudioFunnelSection = {
  id: string;
  enabled: boolean;
  order: number;
  isStandard?: boolean;
  sectionType?: string;
  hasHtmlContent?: boolean;
  templateDbId?: number;
  imageFormat?: string;
  colorMode?: string;
  primaryColor?: string;
  backgroundColor?: string;
  sectionInstructions?: unknown;
};

export type TemplateStudioFunnelConfig = {
  designTemplateType?: string | null;
  sections: TemplateStudioFunnelSection[];
  includeVideoReviewsGifs?: boolean;
};

export const buildVideoReviewsAddonPages = (templateId = 'default'): TemplatePage[] => {
  const sectionUid = sectionUuid('video_reviews_section');
  return [{
    id: sectionUid,
    name: sectionUid,
    type: 'customCodeSection',
    sort: 1.5,
    page: 'productPage',
    runtime: TEMPLATE_STUDIO_RUNTIME,
    renderer: TEMPLATE_STUDIO_RENDERERS.html,
    source: 'generate_video_reviews',
    metadata: definedMetadata({
      template_id: templateId,
      template_source: 'inline',
      template_html: VIDEO_REVIEWS_TEMPLATE_HTML,
      section_role: 'video_reviews',
      ai_process_key: TEMPLATE_STUDIO_AI_PROCESSES.videoReviewsGeneration,
      section_instructions: VIDEO_REVIEWS_SECTION_INSTRUCTIONS,
      billing_addon: ONBOARDING_VIDEO_REVIEWS_GIFS_BILLING_CODE,
      billing_estimated_credits_per_funnel: ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL,
      requested_generated_gif_count: ONBOARDING_VIDEO_REVIEWS_GIFS_GENERATED_COUNT,
    }),
  }];
};

export const buildTemplateStudioPages = (funnel: TemplateStudioFunnelConfig): TemplatePage[] => {
  const pages: TemplatePage[] = [];
  let sortIndex = 0;
  let ctaCount = 1;
  let videoReviewsInserted = false;
  const templateId = funnel.designTemplateType || 'default';

  const pushVideoReviewsSection = () => {
    if (!funnel.includeVideoReviewsGifs || videoReviewsInserted) return;
    const [section] = buildVideoReviewsAddonPages(templateId);
    pages.push({ ...section, sort: sortIndex++ });
    videoReviewsInserted = true;
  };

  const enabledSections = funnel.sections
    .filter((section) => section.enabled)
    .sort((a, b) => a.order - b.order);

  for (const section of enabledSections) {
    if (!section.isStandard && section.sectionType) {
      if (section.hasHtmlContent) {
        const sectionUid = sectionUuid('custom_code_section');
        pages.push({
          id: sectionUid,
          name: sectionUid,
          type: 'customCodeSection',
          sort: sortIndex++,
          page: 'productPage',
          runtime: TEMPLATE_STUDIO_RUNTIME,
          renderer: TEMPLATE_STUDIO_RENDERERS.html,
          source: 'generate_html',
          metadata: definedMetadata({
            template_id: templateId,
            template_db_id: section.templateDbId,
            section_role: section.sectionType,
            ai_process_key: TEMPLATE_STUDIO_AI_PROCESSES.htmlSectionGeneration,
            section_instructions: section.sectionInstructions,
          }),
        });
        if (section.sectionType === 'portada') {
          pushVideoReviewsSection();
        }
      } else {
        const sectionUid = sectionUuid('custom_image_section');
        pages.push({
          id: sectionUid,
          name: sectionUid,
          type: 'customImageSection',
          sort: sortIndex++,
          page: 'productPage',
          runtime: TEMPLATE_STUDIO_RUNTIME,
          renderer: TEMPLATE_STUDIO_RENDERERS.image,
          source: 'generate_image',
          metadata: definedMetadata({
            template_id: templateId,
            template_db_id: section.templateDbId,
            section_role: section.sectionType,
            type_section_image: section.sectionType,
            ai_process_key: TEMPLATE_STUDIO_AI_PROCESSES.sectionImageGeneration,
            image_format: section.imageFormat,
            color_mode: section.colorMode,
            primary_color: section.primaryColor,
            background_color: section.backgroundColor,
          }),
        });
        if (section.sectionType === 'portada') {
          pushVideoReviewsSection();
        }
      }
    } else if (section.isStandard) {
      const mapping = STANDARD_SECTION_MAP[section.id];
      if (!mapping) continue;

      if (mapping.type === 'buyNowFreeShippingSection') {
        ctaCount++;
        pages.push({
          id: `${mapping.pageId}_${ctaCount}`,
          name: `${mapping.pageId}_${ctaCount}`,
          type: mapping.type,
          sort: sortIndex++,
          page: 'productPage',
          runtime: LEGACY_REACT_RUNTIME,
          renderer: 'react',
        });
      } else {
        pages.push({
          id: mapping.pageId,
          name: mapping.pageId,
          type: mapping.type,
          sort: sortIndex++,
          page: 'productPage',
          runtime: LEGACY_REACT_RUNTIME,
          renderer: 'react',
        });
      }
    }
  }

  pushVideoReviewsSection();

  pages.push(
    {
      id: 'header_thankyou',
      name: 'header_thankyou',
      type: 'headerThankyouSection',
      sort: 0,
      page: 'thankyouPage',
      runtime: LEGACY_REACT_RUNTIME,
      renderer: 'react',
      metadata: {
        ai_process_key: 'thankyou_header_content',
      },
    },
    {
      id: 'product_list',
      name: 'product_list',
      type: 'productListSection',
      sort: 1,
      page: 'thankyouPage',
      runtime: LEGACY_REACT_RUNTIME,
      renderer: 'react',
    },
    {
      id: 'button_thankyou',
      name: 'button_thankyou',
      type: 'buttonThankyouSection',
      sort: 2,
      page: 'thankyouPage',
      runtime: LEGACY_REACT_RUNTIME,
      renderer: 'react',
    },
  );

  return pages;
};
